import { useContext, useEffect } from "react";

import { MonitoringEventTypeType } from "../shared/types/monitoring";

import {
  SocketStatsCollector,
  SocketStatsCollectorContext,
  type SocketStatsType,
} from "./SocketStatsCollectorProvider";

import { useAppDispatch } from "store/index";
import { viewStateActions } from "store/slices/view";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";

const PUBSUB_STATS_COLLECTION_INTERVAL = 1000 * 60; // 1 minute
type ConnectionQualityType = "poor" | "good" | "fair" | undefined;

function processSocketStats(
  socketType: SocketStatsType,
  socketStatsCollector: SocketStatsCollector,
  createMonitoringEvent: (
    eventType: typeof MonitoringEventTypeType.PING_STATS,
    contextData: unknown,
  ) => void,
): ConnectionQualityType {
  const stats = socketStatsCollector.getStats(socketType);
  if (!stats?.pingRtt) {
    return;
  }

  createMonitoringEvent(MonitoringEventTypeType.PING_STATS, {
    connection: socketType,
    pingRtt: {
      min: stats.pingRtt.min,
      max: stats.pingRtt.max,
      median: stats.pingRtt.median,
      isHigh: stats.pingRtt.isHigh,
      isLow: stats.pingRtt.isLow,
    },
  });

  const quality: ConnectionQualityType = stats.pingRtt.isHigh
    ? "poor"
    : stats.pingRtt.isLow
      ? "good"
      : "fair";

  return quality;
}

function MonitoringTasks() {
  const dispatch = useAppDispatch();
  const createMonitoringEvent = useCreateMonitoringEvent();
  const socketStatsCollector = useContext(SocketStatsCollectorContext);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!socketStatsCollector) {
        return;
      }

      const pubsubQuality = processSocketStats(
        "pubsub",
        socketStatsCollector,
        createMonitoringEvent,
      );
      const shareDBQuality = processSocketStats(
        "sharedb",
        socketStatsCollector,
        createMonitoringEvent,
      );

      if (!pubsubQuality && !shareDBQuality) {
        return;
      }

      // Combined: either poor → poor; both good → good; else fair. If one missing, use the other.
      let newConnectionQuality: ConnectionQualityType;
      if (pubsubQuality && shareDBQuality) {
        if (pubsubQuality === "poor" || shareDBQuality === "poor") {
          newConnectionQuality = "poor";
        } else if (pubsubQuality === "good" && shareDBQuality === "good") {
          newConnectionQuality = "good";
        } else {
          newConnectionQuality = "fair";
        }
      } else {
        newConnectionQuality = (pubsubQuality ?? shareDBQuality)!;
      }

      dispatch(viewStateActions.setConnectionQuality(newConnectionQuality));

      socketStatsCollector.resetStats();
    }, PUBSUB_STATS_COLLECTION_INTERVAL);

    return () => clearInterval(interval);

    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [dispatch, createMonitoringEvent, socketStatsCollector]);

  return null;
}

export { MonitoringTasks };
